/**
 * Image Upload Service
 * Handles image upload, retrieval, and deletion using the imguplaod.beautello.mv API
 */

// Use HTTPS only for all environments
const BASE_URL = 'https://imguplaod.beautello.mv';

export interface UploadedImage {
  id: string;
  url: string;
  filename: string;
  size?: number;
  uploadedAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface ImageListResponse {
  success: boolean;
  count: number;
  data: UploadedImage[];
}

class ImageUploadService {
  /**
   * Normalize image URL to always use HTTPS
   */
  private normalizeImageUrl(url: string): string {
    if (url.startsWith('http://imguplaod.beautello.mv')) {
      return url.replace('http://imguplaod.beautello.mv', 'https://imguplaod.beautello.mv');
    }
    return url;
  }
  /**
   * Upload a single image file
   */
  async uploadImage(file: File): Promise<ApiResponse<UploadedImage>> {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'Please select a valid image file'
        };
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'File size must be less than 10MB'
        };
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${BASE_URL}/upload`, {
         method: 'POST',
         body: formData,
       });

      const result = await response.json();

      if (response.ok && result.success) {
        // Normalize the returned image URL to ensure HTTPS
        const normalizedData = {
          ...result.data,
          url: this.normalizeImageUrl(result.data.url)
        };
        
        return {
          success: true,
          data: normalizedData,
          message: 'Image uploaded successfully'
        };
      } else {
        return {
          success: false,
          error: result.message || 'Failed to upload image'
        };
      }
    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        error: 'Network error occurred while uploading image'
      };
    }
  }

  /**
   * Delete an image by ID
   */
  async deleteImage(imageId: string): Promise<ApiResponse<void>> {
    try {
      console.log('Attempting to delete image ID:', imageId);
      const deleteUrl = `${BASE_URL}/image/${imageId}`;
      console.log('Delete URL:', deleteUrl);
      
      const response = await fetch(deleteUrl, {
         method: 'DELETE',
       });

      console.log('Delete response status:', response.status);
      const result = await response.json();
      console.log('Delete response body:', result);

      if (response.ok && result.success) {
        return {
          success: true,
          message: 'Image deleted successfully'
        };
      } else {
        return {
          success: false,
          error: result.message || result.error || 'Failed to delete image'
        };
      }
    } catch (error) {
      console.error('Image delete error:', error);
      return {
        success: false,
        error: 'Network error occurred while deleting image'
      };
    }
  }

  /**
   * Get image URL by ID
   */
     getImageUrl(imageId: string): string {
     return `${BASE_URL}/image/${imageId}`;
   }

  /**
   * List all uploaded images
   */
  async listImages(): Promise<ImageListResponse> {
    try {
      const response = await fetch(`${BASE_URL}/images`);
      const result = await response.json();

      if (response.ok && result.success) {
        return {
          success: true,
          count: result.count,
          data: result.data
        };
      } else {
        return {
          success: false,
          count: 0,
          data: []
        };
      }
    } catch (error) {
      console.error('List images error:', error);
      return {
        success: false,
        count: 0,
        data: []
      };
    }
  }

  /**
   * Health check for the image upload service
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${BASE_URL}/`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(files: File[]): Promise<ApiResponse<UploadedImage[]>> {
    try {
      const uploadPromises = files.map(file => this.uploadImage(file));
      const results = await Promise.all(uploadPromises);
      
      const successfulUploads: UploadedImage[] = [];
      const errors: string[] = [];
      
      results.forEach((result, index) => {
        if (result.success && result.data) {
          successfulUploads.push(result.data);
        } else {
          errors.push(`File ${files[index].name}: ${result.error}`);
        }
      });

      if (successfulUploads.length === 0) {
        return {
          success: false,
          error: `All uploads failed: ${errors.join(', ')}`
        };
      }

      return {
        success: true,
        data: successfulUploads,
        message: `${successfulUploads.length} of ${files.length} images uploaded successfully${errors.length > 0 ? `. Errors: ${errors.join(', ')}` : ''}`
      };
    } catch (error) {
      console.error('Multiple upload error:', error);
      return {
        success: false,
        error: 'Failed to upload multiple images'
      };
    }
  }

  /**
   * Extract image ID from URL (specifically for imguplaod.beautello.mv)
   */
  extractImageId(imageUrl: string): string | null {
    try {
      console.log('Extracting image ID from URL:', imageUrl);
      console.log('BASE_URL:', BASE_URL);
      console.log('URL starts with BASE_URL?', imageUrl.startsWith(BASE_URL));
      
      // Check if it's from our domain
      if (imageUrl.startsWith(BASE_URL)) {
        // Handle full URLs like https://imguplaod.beautello.mv/image/12345
        if (imageUrl.includes('/image/')) {
          const parts = imageUrl.split('/image/');
          const extractedId = parts[1] || null;
          console.log('Extracted ID:', extractedId);
          return extractedId;
        }
      }
      
      // If it's just an ID (fallback)
      if (!imageUrl.includes('/') && !imageUrl.includes('http')) {
        console.log('Using URL as ID directly:', imageUrl);
        return imageUrl;
      }
      
      console.log('Could not extract ID from URL');
      return null;
    } catch (error) {
      console.error('Error extracting image ID:', error);
      return null;
    }
  }

  /**
   * Check if image URL is from our upload service
   */
  isFromOurService(imageUrl: string): boolean {
    return imageUrl.startsWith(BASE_URL) || imageUrl.startsWith('http://imguplaod.beautello.mv');
  }

  /**
   * Normalize any image URL to use HTTPS (public method for existing URLs)
   */
  normalizeUrl(url: string): string {
    return this.normalizeImageUrl(url);
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();
export default imageUploadService;
